import {
  Directive,
  Input,
  Output,
  EventEmitter,
  ComponentRef,
  ViewContainerRef,
  SimpleChanges,
  Type,
} from '@angular/core';
import { ComponentLoader } from 'dg-common-directives-lib';
import { Observable, combineLatest } from 'rxjs';
import { startWith, pairwise, map } from 'rxjs/operators';
import { ComponentLoaderService } from './component-loader.service';

interface ComponentInputs {
  [key: string]: unknown;
}

export interface ComponentOutputs {
  from: string;
  data: unknown;
}

export class ComponentConfig {
  loadAsync: boolean = true;
  componentAddress: string | Type<unknown>;
  componentInputs?: { [key: string]: unknown } = {};
  componentOutputs?: { [key: string]: (event: unknown) => unknown } = {};
  componentOutputIdsValue?: string[] = this.componentOutputIds;
  get componentOutputIds() {
    return this.componentOutputs && Object.keys(this.componentOutputs);
  }
  output = (event: ComponentOutputs[]) =>
    event.forEach(({ from, data }) => this.componentOutputs[from](data));
}

@Directive({
  selector: '[componentLoader]',
})
export class ComponentLoaderDirective {
  @Input('componentLoader') componentAddress: string | Type<unknown>;
  @Input() isAsync = true;
  @Input() componentInputs: ComponentInputs = {};
  @Input() componentOutputs: string[] = [];
  @Output() output: EventEmitter<ComponentOutputs[]>;

  componentRef: ComponentRef<unknown>;

  getRequestedComponent(): any {
    switch (this.componentAddress as string) {
      case 'ReportInputPage':
        return import(
          `src/app/digigov/dg_components/dg-report/dg-report-output/dg-report-output.component`
        ).then((c) => c.DgReportOutputComponent);
      /* case 'CaseViewComponent': return import(
      'src/app/digigov/dg_core/case-view/case-view.component'
    ).then((c) => c.CaseViewComponent); */
      case 'CaseViewComponent':
        return import(
          `src/app/digigov/dg_core/dg-case-view/dg-case-view.component`
        ).then((c) => c.DgCaseViewComponent);
      case 'ViewDakComponent':
        return import(
          `src/app/digigov/dg_core/correspondence/components/view-dak/view-dak.component`
        ).then((c) => c.ViewDakComponent);
      case 'SupportCaseView':
        return import(
          'src/app/digigov/dg_core/support-request-module/admin-support-screen/worklist-details/worklist-details.component'
        ).then((c) => c.WorklistDetailsComponent);
      case 'IntimationDtls':
        return import(
          'src/app/digigov/dg_core/notification-view/notification-view.component'
        ).then((c) => c.NotificationViewComponent);
      case 'AppGenComponent':
        return import(
          'src/app/digigov/dg_core/app-gen/app-gen-main/app-gen-main.component'
        ).then((c) => c.AppGenMainComponent);
      case 'AppGenMainComponent':
        return import(
          'src/app/digigov/dg_core/app-gen/app-gen-main/app-gen-main.component'
        ).then((c) => c.AppGenMainComponent);
      case 'GenericInfoComponent':
        return import(
          'src/app/digigov/dg_core/generic-info/generic-info.component'
        ).then((c) => c.GenericInfoComponent);
      case 'RequestCreationComponent':
        return import(
          'src/app/digigov/dg_core/request-creation/request-creation.component'
        ).then((c) => c.RequestCreationComponent);
      case 'EmsEmployeeDetailsComponent':
        return import(
          'src/app/digigov/dg_core/EMS/ems-employee-details/ems-employee-details.component'
        ).then((c) => c.EmsEmployeeDetailsComponent);
      case 'TaggedActionFormComponent':
        return import(
          'src/app/digigov/dg_core/remarks-actions/tagged-action-form/tagged-action-form.component'
        ).then((c) => c.TaggedActionFormComponent);
      case 'groupWorklistComponent':
        return import(
          'src/app/digigov/dg_core/group-worklist/group-worklist.component'
        ).then((c) => c.GroupWorklistComponent);

      case 'awaitComponent':
        return import(
          'src/app/digigov/dg_core/actions/dg-await/dg-await.component'
        ).then((c) => c.DgAwaitComponent);
    }
  }

  constructor(
    private componentLoaderService: ComponentLoaderService,
    public viewContainerRef: ViewContainerRef
  ) {
    this.output = new EventEmitter();
  }

  ngOnInit(): void {
    // this.intiallizeComponent();
  }

  intiallizeComponent() {
    if (this.componentAddress) {
      if (this.isAsync)
        this.componentLoaderService
          .instantiateComponentAsync(this.viewContainerRef, {
            loadChildren: () =>
              this.getRequestedComponent().catch((e) =>
                console.error('Could not load component')
              ),
          } as ComponentLoader)
          .subscribe((componentRef) => {
            this.componentRef = componentRef;
            this.setInputs();
            this.setOutputs();
          });
      else {
        this.componentRef = this.componentLoaderService.instantiateComponentSync(
          this.viewContainerRef,
          this.componentAddress as Type<unknown>
        );
        this.setInputs();
        this.setOutputs();
      }
    }
  }
  ngOnChanges(sc: SimpleChanges): void {
    if (sc?.componentAddress) {
      this.viewContainerRef?.clear();
      this.intiallizeComponent();
      return;
    }
    if (this.componentRef?.instance) this.setInputs();
  }

  setInputs = () => {
    Object.keys(this.componentInputs).forEach(
      (key) => (this.componentRef.instance[key] = this.componentInputs[key])
    );
    (this.componentRef?.instance as any)?.ngOnChanges?.();
  };

  setOutputs = () => {
    let outputIds = [];
    this.componentOutputs?.forEach((e) => {
      if (this.componentRef?.instance?.[e]) {
        outputIds?.push(e);
      }
    });
    const outputs = outputIds?.map((key) =>
      (this.componentRef.instance?.[key] as Observable<unknown>).pipe(
        startWith({ from: key, data: null }),
        map((data) => ({ from: key, data }))
      )
    );
    combineLatest(outputs)
      .pipe(
        pairwise(),
        map(([prev, curr]) => curr.filter((e) => !prev.includes(e)))
      )
      .subscribe((data) => {
        this.output.emit(data);
      });
  };
}
